/* script */

import ApplicationState            from 'g3w-state';
import GUI                         from 'g3w-app';
import { getUniqueDomId }          from 'utils/getUniqueDomId';
import { createFilterFromString }  from 'utils/createFilterFromString';
import { XHR }                     from 'utils/XHR';
import { getCatalogLayerById }     from 'utils/getCatalogLayerById';
import { gettext as _ }            from 'g3w-i18n';

const __vue_script__ = {

  /** @since 3.8.6 */
  name: 'query-builder',

  data() {
    return {
      edit:         undefined !== this.$options.options,
      currentlayer: null,
      message:      '',
      filter:       this.$options?.options?.filter ?? '',
      loading: {
        test:   false,
        values: false
      },
      values:      [],
      manual:      true,
      manualvalue: null,
      select: {
        field: null,
        value: null
      },
    }
  },

  computed:{

    fields() {
      return this?.currentlayer?.fields ?? [];
    },

    disabled() {
      return !this.filter;
    },

  },

  watch: {

    'select.field'() {
      this.values = [];
      this.manual = true;
    },

  },

  methods: {

    addToExpression({ value, type } = {}) {
      switch(type) {
        case 'operator': value = ` ${value} `; break;
        case 'field':    value = `"${value}"`; break;
        case 'value':    value = `'${value}'`; break;
      }
      if (value) this.filter = (`${this.filter}${value}`);
    },

    /**
     * ORIGINAL SOURCE: src/services/querybuilder.js@v3.9.3
     */
    async all() {
      this.loading.values = true;
      try {
        let layerId    = this.currentlayer.id;
        let field      = this.select.field;
        let CACHE      = ApplicationState.querybuilder.cache;
        CACHE[layerId] = CACHE[layerId] || {};
        if (undefined !== CACHE[layerId][field]) {
          this.values = CACHE[layerId][field];
        } else {
          const response = await XHR.get({
            url: getCatalogLayerById(layerId).getUrl('data'),
            params: { ordering: field, formatter: 1, fformatter: field }
          });
          if (response.result) {
            CACHE[layerId][field] = CACHE[layerId][field] || response.data;
          }
          this.values = CACHE[layerId][field] || [];
        }
      } catch(e) {
        console.warn(e);
      }
      this.loading.values = false;
      await this.$nextTick();
      this.manualvalue    = null;
      this.manual         = false;
    },

    reset() {
      this.filter                 = '';
      this.message                = '';
      this.filterElement.previous = null;
      this.filterElement.current  = null;
      this.filterElement.operator = null;
      this.select.field           = null;
      if (this.$refs.search_fields) { this.$refs.search_fields.selectedIndex = -1 }
      if (this.$refs.search_values) { this.$refs.search_values.selectedIndex = -1 }
    },

    /**
     * ORIGINAL SOURCE: src/services/querybuilder.js@v3.9.3
     */
    async run() {
      try {
        this.loading.test = true;
        const layer       = getCatalogLayerById(this.currentlayer.id);
        const { data }    = await GUI.getData('search:features', {
          inputs: {
            layer,
            filter:        createFilterFromString({ layer, filter: this.filter }),
            feature_count: 100,
          },
          outputs: true,
        });
        const n           = data.length && data[0].features.length; // number of features
        this.message      = undefined === n ? '' : ` ${n}`;
        return data;
      } catch(e) {
        console.warn(e);
      } finally {
        this.loading.test = false;
      }
    },

    /**
     * ORIGINAL SOURCE: src/services/querybuilder.js@v3.9.3
     */
    async save() {
      const id      = this.projectId || ApplicationState.project.getId();
      const edit_id = this.edit && this.$options.options.id;
      const item    = window.localStorage.getItem('QUERYBUILDERSEARCHES');
      //get stored query builder searches
      let searches  = item ? JSON.parse(item) : undefined;

      let query;

      try {
        query = {
          layerId:   this.currentlayer.id,
          filter:    this.filter,
          layerName: getCatalogLayerById(this.currentlayer.id).getName(),
          name:      edit_id ? (this.edit && this.$options.options.name) : (await GUI.prompt(_('Insert the name of the new search'), '')),
          id:        edit_id || getUniqueDomId(),
        };

        // edit local item
        if (edit_id) {
          const i = searches[id].findIndex(s => query.id === s.id);
          if (-1 !== i) {
            searches[id][i] = query;
          }
        }

        // add local item
        else {
          GUI.getService('search').state.querybuildersearches.push(query); // add query builder search
          if (undefined === searches) {
            searches     = { [id]: [query] };
          } else {
            searches[id] = [...(searches[id] || []), query];
          }
        }
      } catch(e) {
        console.warn(e);
        return;
      }

      // reset items
      const ITEMS = ApplicationState.querybuilder.searches;

      ITEMS[id]   = ITEMS[id] ?? [];
      
      try {
        window.localStorage.setItem('QUERYBUILDERSEARCHES', JSON.stringify(searches));
      } catch(e) {
        console.warn(e);
      }

      setTimeout(() => { searches[id].forEach(q => ITEMS[id].push(q)); }, 0);
      ITEMS[id].splice(0);
      GUI.showUserMessage({ type: 'success', message: _('Saved'), autoclose: true });
    },

    /**
     * @since 4.1.0
     */
    onCurrentlayerChange(e) {
      this.currentlayer = this.layers[e.target.value];
      this.select.field = null;
      this.select.value = null;
      this.reset();
    },

  },

  created() {

    this.filterElement = {
      current:  null,
      previous: null,
      operator: null
    };

    const project = ApplicationState.project;

    this.layers   = project
      .getLayers()
      .filter(l => !l.baselayer && Array.isArray(l.fields))
      .map(layer => {
        // exclude join fields
        let exclude = [];
        project.state.relations
          .filter(r => layer.id === r.referencedLayer && 'ONE' === r.type) // get relations by layerId
          .forEach(r => {
            const l = project.getLayerById(r.referencingLayer);
            r.customPrefix = r?.customPrefix ?? `${l.getName()}_`;
            exclude = [...exclude, ...l.getFields().map(({ name }) => `${r.customPrefix}${name}`)];
          });
        return {
          id:     layer.id,
          label:  layer.title,
          fields: layer.fields.filter(f => f.show).map(({ label, name }) => ({ label, name })).filter(f => !exclude.includes(f))
        }
      });

    this.currentlayer = this.edit ? this.layers.find(l => this.$options.options.layerId === l.id) : this.layers[0];

  },

};


/* template */
var render = function render(){var _vm=this,_c=_vm._self._c;return _c('div',{staticClass:"form-group",attrs:{"id":"query_builder"}},[_c('div',{staticClass:"mb-5",attrs:{"id":"query_builder_layers"}},[_c('label',[_vm._v(_vm._s(_vm.$t('EXPRESSION')))]),_vm._v(" "),_c('a',{staticStyle:{"float":"right"},attrs:{"href":`https://g3w-suite.readthedocs.io/en/v3.9.x/g3wsuite_client.html#search-and-query-builder`,"target":"_blank","data-i18n-title":"Docs","data-placement":"right"}},[_c('i',{staticClass:"fa fa-external-link-alt",attrs:{"aria-hidden":"true"}})]),_vm._v(" "),_c('x-select',{staticStyle:{"color":"#000"},attrs:{"value":Math.max(_vm.layers.indexOf(_vm.currentlayer), 0),"searchable":""},on:{"change":_vm.onCurrentlayerChange}},_vm._l((_vm.layers),function(layer,i){return _c('x-option',{key:layer.label,attrs:{"value":i}},[_vm._v(_vm._s(layer.label))])}),1)],1),_vm._v(" "),_c('textarea',{directives:[{name:"model",rawName:"v-model",value:(_vm.filter),expression:"filter"}],attrs:{"id":"query_builder_expression_content"},domProps:{"value":(_vm.filter)},on:{"input":function($event){if($event.target.composing)return;_vm.filter=$event.target.value}}}),_vm._v(" "),(_vm.loading.test)?_c('div',{staticClass:"bar-loader",staticStyle:{"border":"0"}}):_vm._e(),_vm._v(" "),_c('b',{directives:[{name:"show",rawName:"v-show",value:(_vm.message),expression:"message"}],staticClass:"skin-color"},[_vm._v(_vm._s(_vm.$t('Features found:'))+" "+_vm._s(_vm.message))]),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","flex-wrap":"wrap","justify-content":"flex-end","margin-top":"5px"}},[_c('button',{staticClass:"query_builder_button btn btn-secondary bold",attrs:{"disabled":_vm.disabled},on:{"click":_vm.run}},[_c('i',{staticClass:"fas fa-play",staticStyle:{"color":"green"},attrs:{"aria-hidden":"true"}}),_vm._v("\n      "+_vm._s(_vm.$t('RUN'))+"\n    ")]),_vm._v(" "),_c('button',{staticClass:"query_builder_button btn btn-secondary bold",on:{"click":_vm.reset}},[_c('i',{staticClass:"fas fa-broom",attrs:{"aria-hidden":"true"}}),_vm._v("\n      "+_vm._s(_vm.$t('CLEAR'))+"\n    ")]),_vm._v(" "),_c('button',{staticClass:"query_builder_button btn btn-secondary bold",attrs:{"disabled":_vm.disabled},on:{"click":_vm.save}},[_c('i',{staticClass:"far fa-save",attrs:{"aria-hidden":"true"}}),_vm._v("\n      "+_vm._s(_vm.$t('SAVE'))+"\n    ")])]),_vm._v(" "),_c('hr'),_vm._v(" "),_c('label',[_vm._v(_vm._s(_vm.$t('FIELDS')))]),_vm._v(" "),_c('select',{ref:"search_fields",staticClass:"mb-5",attrs:{"size":"4"}},[_c('option',{attrs:{"selected":"","hidden":""}}),_vm._v(" "),_vm._l((_vm.fields),function(field){return _c('option',{key:field.name,on:{"click":function($event){_vm.select.field = field.name; _vm.addToExpression({ value: field.name, type: 'field' })}}},[_vm._v(_vm._s(field.label))])})],2),_vm._v(" "),_c('div',{staticClass:"mb-5",staticStyle:{"display":"flex","flex-wrap":"wrap"}},_vm._l((['>=', '<=', '!=', '=', '>', '<', 'IN', 'LIKE', 'ILIKE', 'AND', 'OR' ]),function(operator){return _c('button',{key:operator,staticClass:"query_builder_button btn btn-secondary bold",on:{"click":function($event){return _vm.addToExpression({ value: operator, type: 'operator' })}}},[_vm._v(_vm._s(operator))])}),0),_vm._v(" "),(_vm.loading.values)?_c('div',{staticClass:"bar-loader",staticStyle:{"border":"0"}}):_vm._e(),_vm._v(" "),(!_vm.manual)?_c('select',{ref:"search_values",staticClass:"mb-5",attrs:{"size":"4"}},[_c('option',{attrs:{"selected":"","hidden":""}}),_vm._v(" "),_vm._l((_vm.values),function([key, value]){return _c('option',{key:key,on:{"click":function($event){_vm.select.value = key; _vm.addToExpression({ value: key, type: 'value' })}}},[_vm._v(_vm._s(value))])})],2):_vm._e(),_vm._v(" "),(_vm.select.field !== null && !_vm.values.length)?_c('button',{staticClass:"btn btn-secondary bold",class:{'skin-border-color' : !_vm.manual },staticStyle:{"color":"#000"},on:{"click":_vm.all}},[_c('i',{staticClass:"fas fa-search",attrs:{"aria-hidden":"true"}}),_vm._v("\n    "+_vm._s(_vm.$t('SEARCH A VALUE'))+"\n  ")]):_vm._e()])
}
var staticRenderFns = []

const __vue_render__ = render;
const __vue_staticRenderFns__ = staticRenderFns;

const __vue_component__ = __vue_script__;
__vue_component__.render = __vue_render__;
__vue_component__.staticRenderFns = __vue_staticRenderFns__;
__vue_component__._compiled = true;
__vue_component__.__file = "/home/volterra79/G3W-SUITE/g3w-client/src/components/QueryBuilder.vue";
__vue_component__._scopeId = "data-v-04ed6ee9";
/* style inject */
import __vue_create_injector__ from 'g3w-vue:style-injector';
[{"id":"04ed6ee9-0","css":"#query_builder[data-v-04ed6ee9]{margin-bottom:0;height:100%;display:flex;flex-wrap:nowrap;flex-direction:column}select[data-v-04ed6ee9]{background-color:#fff;color:#000;border:none}option[data-v-04ed6ee9]{padding:8px;cursor:pointer}option[data-v-04ed6ee9]:checked{background:var(--skin-color) linear-gradient(0deg,var(--skin-color) 0%,var(--skin-color) 100%);color:#fff}option[data-v-04ed6ee9]:nth-of-type(odd){background-color:#f9f9f9}.query_builder_button[data-v-04ed6ee9]{margin:1px;flex-basis:78px;flex-grow:1;color:#000}.mb-5[data-v-04ed6ee9]{margin-bottom:5px}#query_builder_expression_content[data-v-04ed6ee9]{width:100%;resize:none;height:100px;color:#000}\n","media":"","filename":"src/components/QueryBuilder.vue"}].forEach(__vue_create_injector__());
export default __vue_component__;